// ABOUTME: SPEC-76 — the read-first panel under the Enter Boutique button: Pedro's tutorial copy, static,
// ABOUTME: so a first-time guest can read how the boutique works BEFORE entering. Never blocks entering.

import React from 'react';
import useScrollColumn from './useScrollColumn';
import { useBreakpoint } from '../Projectionist/useViewportSize';
import { RAIL_UNIT, u } from '../ReelChrome/railGeometry';
import { MOBILE_UNIT, mu, muMin } from '../ReelChrome/mobileGeometry';

// One size for the whole card, in the reel chrome's own authored pixels (`--u`), with a real-px floor so
// it stays readable on a small handset (the mobile unit alone would set it at ~9px).
const TEXT_AUTHORED = 34;
const DESKTOP_MIN_PX = 15;   // 1080p and up (2026-09-29 matrix pass): 14px read small beside the 21px headline
const MOBILE_MIN_PX = 12;   // the caption floor (CaptionOverlay CAPTION_MIN_PX): body copy, not a label
// How far the card may reach up: from the bottom edge to just under the centred Enter panel. The panel is
// centred in the screen and about 130 authored px tall on either side of the midline; 84px covers that on
// every viewport this gate renders at, plus the gap, so the card scrolls inside itself rather than ever
// touching the button above it. (Real iPhone-14 pass 2026-09-29: at 96px the last paragraph and the closing question
// were scrolled out of sight; 84 with a 12px floor shows all of it, the SE still scrolls.)
const RESERVED_ABOVE_PX = 84;

function boxStyle(isMobile, extraReserve = 0) {
  return {
    '--u': isMobile ? MOBILE_UNIT : RAIL_UNIT,
    bottom: `max(16px, calc(env(safe-area-inset-bottom, 0px) + 16px))`,
    width: 'min(92vw, 44rem)',
    // SPEC-82: authored units the Enter panel gained (voice hint / How it works); 0 => today's expression, byte for byte.
    maxHeight: extraReserve
      ? `calc(50dvh - ${RESERVED_ABOVE_PX}px - ${(isMobile ? mu : u)(extraReserve)})`
      : `calc(50dvh - ${RESERVED_ABOVE_PX}px)`,
    padding: isMobile ? mu(40) : 'max(14px, calc(37.333 * var(--u)))',
    fontSize: isMobile ? muMin(TEXT_AUTHORED, MOBILE_MIN_PX) : `max(${DESKTOP_MIN_PX}px, calc(${TEXT_AUTHORED} * var(--u)))`,
  };
}

/**
 * @param {{ copy: { title: string, paragraphs: string[], closing: string, primerPrompt: string } }} props
 *   `copy` is `readCopy(config.first_visit_tutorial)` — tenant data, never a brand literal in the component.
 *   `extraReserve` / `id` (SPEC-82, both optional): authored units the panel above gained, and the id a "How it works" line controls.
 */
function TutorialPrimer({ copy, extraReserve = 0, id }) {
  const isMobile = useBreakpoint() === 'mobile';
  const [columnRef, fade] = useScrollColumn();
  return (
    <section
      id={id}
      aria-labelledby={copy.primerPrompt ? 'tutorial-primer-heading' : undefined}
      aria-label={copy.primerPrompt ? undefined : copy.title}
      data-testid="tutorial-primer"
      data-composition={isMobile ? 'mobile' : 'desktop'}
      className="absolute left-1/2 -translate-x-1/2 z-10 flex flex-col text-center rounded-reel-gate
        bg-reel-chrome-panel backdrop-blur-reel-chrome text-reel-chrome-textPrimary ui-reveal-fade"
      style={boxStyle(isMobile, extraReserve)}
    >
      {/* The card never scrolls; this column does, INSIDE the card's padding, so a cut line ends well clear of the
          rounded edge and fades out instead of being sliced. It is the keyboard-focusable scroller. */}
      <div
        ref={columnRef}
        tabIndex={0}
        data-testid="tutorial-primer-scroll"
        className="min-h-0 overflow-y-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded-md"
        style={fade}
      >
        {copy.primerPrompt && (
          <h2 id="tutorial-primer-heading" className="m-0 font-reel-heading font-bold tracking-wide" style={{ fontSize: '1em' }}>
            {copy.primerPrompt}
          </h2>
        )}
        <p className="m-0 mt-2 font-reel-heading font-bold" style={{ lineHeight: 1.35 }}>{copy.title}</p>
        {copy.paragraphs.map((text) => (
          <p key={text} className="m-0 mt-1.5 font-reel-body" style={{ lineHeight: isMobile ? 1.35 : 1.45 }}>{text}</p>
        ))}
        {/* The closing line is not shown on the gate (owner 2026-09-30); the agent still speaks it after entering. */}
      </div>
    </section>
  );
}

// The style builder is exported for tests only: jsdom drops every calc()/var()/max() value from an element's style, so
// the responsive budgets (src/__tests__/tutorialResponsiveMatrix.test.js) are asserted on the object itself.
export { boxStyle };
export default TutorialPrimer;
